import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { decodeJwt } from "./jwt";

export interface Session {
    token: string;
    user: string | null;
    domain: string | null;
    isDomainAdmin: boolean;
    // Seconds-since-epoch from the JWT's `exp` claim, or null if the token didn't carry one.
    // Kept as a plain number (rather than a stored "expired" boolean) so expiry is always
    // evaluated against the current time instead of going stale.
    exp: number | null;
}

export interface LoginState {
    sessions: Session[];
    // Token of the session currently driving currentUser/currentToken/userLoggedIn. null when
    // there are no sessions at all.
    activeToken: string | null;
}

const initialState: LoginState = {
    sessions: [],
    activeToken: null
};

function sessionFromToken(token: string): Session {
    const claims = decodeJwt(token);
    return {
        token,
        user: claims?.user ?? claims?.username ?? null,
        domain: claims?.domain ?? null,
        isDomainAdmin: claims?.isDomainAdmin === true,
        exp: typeof claims?.exp === "number" ? claims.exp : null
    };
}

export const loginSlice = createSlice({
    name: 'login',
    initialState,
    reducers: {
        // Logs a session in and makes it active, without disturbing any other already-logged-in
        // sessions - this is what both the normal login form and the "Add another account" /
        // re-auth flows dispatch. A second login for the same user@domain (e.g. re-authenticating
        // an expired session) replaces that session's token in place rather than duplicating it.
        login: (state, action: PayloadAction<string>) => {
            const session = sessionFromToken(action.payload);
            const existingIndex = session.user === null
                ? -1
                : state.sessions.findIndex((s) => s.user === session.user && s.domain === session.domain);
            if (existingIndex >= 0) {
                state.sessions[existingIndex] = session;
            } else {
                state.sessions.push(session);
            }
            state.activeToken = session.token;
        },
        // Logs out the currently active session only. Any other logged-in accounts remain and one
        // of them (if any) automatically becomes active so the user isn't dropped to /login while
        // still holding a valid session elsewhere.
        logout: (state) => {
            state.sessions = state.sessions.filter((s) => s.token !== state.activeToken);
            state.activeToken = state.sessions[0]?.token ?? null;
        },
        // Makes an already-logged-in session active. No-ops if the token doesn't match a known
        // session (e.g. it was already logged out elsewhere).
        switchAccount: (state, action: PayloadAction<string>) => {
            if (state.sessions.some((s) => s.token === action.payload)) {
                state.activeToken = action.payload;
            }
        }
    }
});

export const { login, logout, switchAccount } = loginSlice.actions;

export const isSessionExpired = (session: Session) => session.exp !== null && Date.now() >= session.exp * 1000;

export const sessions = (state: RootState) => state.login.sessions;

export const activeSession = (state: RootState): Session | null =>
    state.login.sessions.find((s) => s.token === state.login.activeToken) ?? null;

// The other logged-in sessions, for the header's account switcher.
export const otherSessions = (state: RootState): Session[] =>
    state.login.sessions.filter((s) => s.token !== state.login.activeToken);

export const userLoggedIn = (state: RootState) => activeSession(state) !== null;

// redux-persist injects `_persist.rehydrated` onto the root state once the persisted
// store has finished restoring from localStorage. Until that happens, `state.login`
// is just the reducer's initial value (no sessions) and must not be read as "logged out" - doing
// so races PersistGate's own (unreliable) render-blocking and can bounce a logged-in user
// to /login on refresh. Stores without persistence (e.g. tests) have no `_persist` key at
// all, so default to true - there's nothing to wait for.
export const sessionRehydrated = (state: RootState) => (state as any)._persist?.rehydrated ?? true;

export const currentToken = (state: RootState) => activeSession(state)?.token ?? null;

export const currentUser = (state: RootState) => activeSession(state)?.user ?? null;

export const isDomainAdmin = (state: RootState) => activeSession(state)?.isDomainAdmin ?? false;

export default loginSlice.reducer;
